// src/features/analytics/audienceValues.ts
//
// Pure display helpers shared by the Audience tab and the Content-tab media
// Drawer (extracted from AnalyticsAudiencePage in Seq U-5 so the two surfaces
// cannot drift).

import {
  ANALYTICS_METRIC_LABELS,
  type AnalyticsDimension,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import { countryName, regionLabel } from './regionLabels';

export const DIMENSION_LABELS: Record<AnalyticsDimension, string> = {
  country: 'Country',
  region: 'Region',
  city: 'City',
  deviceType: 'Device type',
  platform: 'Platform',
  streamType: 'Stream type',
};

/**
 * Display label for a dimension row value. country arrives as the ISO-3166-1
 * alpha-2 code and renders as the full English name via Intl.DisplayNames
 * (raw-code fallback) — codes collide with mental models ('MA' is Morocco,
 * not Massachusetts, one tab from a US-states region view); region arrives as
 * the full ISO-3166-2 code since U-3 (ADR-015) and is humanized via
 * regionLabels; city arrives as the render-ready compound 'City, Region,
 * Country' and renders as-is (compact codes stay — full names would bloat the
 * rows and country context is inline) except that empty components collapse
 * ('Unknown, , ' — the empty-geo row — reads as plain 'Unknown'); an empty
 * value on any dimension renders as Unknown rather than a blank cell.
 */
export function valueLabel(dimension: AnalyticsDimension, value: string): string {
  if (dimension === 'country') return value === '' ? 'Unknown' : countryName(value) ?? value;
  if (dimension === 'region') return regionLabel(value);
  if (dimension === 'city') return cityLabel(value);
  return value === '' ? 'Unknown' : value;
}

/** Collapse empty components of the compound city value; '' stays Unknown. */
function cityLabel(value: string): string {
  if (value === '') return 'Unknown';
  const parts = value.split(', ').filter((part) => part !== '');
  return parts.length > 0 ? parts.join(', ') : 'Unknown';
}

/** Watch time charts in whole minutes (counts chart as-is). */
export function chartSeriesLabel(metric: AnalyticsMetric): string {
  return metric === 'watchTime' ? 'Watch time (min)' : ANALYTICS_METRIC_LABELS[metric];
}

export function metricValue(
  row: { sessions: number; watchTimeMs: number; uniqueViewers: number },
  metric: AnalyticsMetric
): number {
  if (metric === 'sessions') return row.sessions;
  if (metric === 'uniqueViewers') return row.uniqueViewers;
  return row.watchTimeMs;
}
